import { supabase } from "@/integrations/supabase/client";

/**
 * Edge Function aufrufen und die ECHTE Fehlermeldung zurueckbekommen.
 *
 * WARUM ES DAS GIBT: supabase.functions.invoke() liefert bei jedem Nicht-2xx-Status
 * denselben Satz — „Edge Function returned a non-2xx status code". Was der Server
 * tatsaechlich geantwortet hat, steckt in error.context, einer Response, die man
 * erst auslesen muss. Wer das nicht tut, zeigt dem Kunden eine Meldung, aus der
 * niemand etwas ableiten kann, und sich selbst auch.
 *
 * Genau daran haben wir am 27.08.2026 einen halben Abend verloren: Das Stornieren
 * scheiterte an einem ungueltigen Enum-Wert, und die Meldung darueber war
 * „Fehler beim Stornieren". Am 28.08. dasselbe im Kundenportal, diesmal als
 * „Edge Function returned a non-2xx status code".
 *
 * Von rund zwei Dutzend invoke()-Aufrufen im Frontend packten acht die Meldung aus,
 * jeder mit eigener Kopie derselben try/catch-Zeilen. Das hier ist die eine Stelle.
 *
 * Wirft mit der Servermeldung, wenn eine da ist — sonst mit einer deutschen Ersatzmeldung.
 */
export async function invokeEdgeFunction<T = any>(
  name: string,
  body: Record<string, unknown> = {},
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });

  if (error) {
    throw new Error(await edgeFunctionErrorMessage(error, name));
  }

  // Manche Functions antworten mit 200 UND einem error-Feld (bewusst: der Aufruf lief,
  // die Fachlogik lehnte ab). Auch das ist ein Fehler fuer den Aufrufer.
  if ((data as { error?: string } | null)?.error) {
    throw new Error(String((data as { error: string }).error));
  }
  return data as T;
}

/**
 * Macht aus dem error von supabase.functions.invoke() eine Meldung, die ein Kunde
 * lesen kann. Reihenfolge: `error` aus dem JSON-Body (unsere Functions) → deutsche
 * Ersatzmeldung je nach Fehlerart. Die englischen Saetze von functions-js erreichen
 * den Kunden nie.
 *
 * WARUM DIE ERSATZMELDUNG: Nicht jede Nicht-2xx-Antwort kommt aus unserem Code. Bei
 * Zeitueberschreitung, Kaltstart- oder Ressourcenfehlern antwortet das Gateway mit
 * `{ code, message }` oder gar keinem JSON — dann gibt es kein `error`-Feld. Fall
 * Julia B. 16.09.2026: Guthaben-Buchung scheiterte, der Kunde sah nur
 * „Edge Function returned a non-2xx status code", wir hatten keinen Anhaltspunkt.
 * Status + Rohantwort landen deshalb in der Konsole (Sentry-Breadcrumb).
 */
export async function edgeFunctionErrorMessage(error: unknown, functionName = "edge-function"): Promise<string> {
  const err = error as { name?: string; message?: string; context?: Response };

  if (err?.name === "FunctionsFetchError") {
    console.error(`[${functionName}] Anfrage nicht angekommen:`, err.message);
    return "Keine Verbindung zum Server. Bitte prüfe deine Internetverbindung und versuche es noch einmal.";
  }

  let status: number | undefined;
  let rohtext = "";
  try {
    // context ist eine Response — nur einmal lesbar, deshalb als Text lesen und selbst parsen.
    status = err?.context?.status;
    rohtext = (await err?.context?.text?.()) ?? "";
    const antwort = rohtext ? JSON.parse(rohtext) : null;
    if (antwort?.error) return String(antwort.error);
  } catch {
    /* Body war kein JSON oder schon gelesen — dann greift die Ersatzmeldung. */
  }

  console.error(`[${functionName}] Fehler ohne Servermeldung:`, {
    name: err?.name,
    status,
    antwort: rohtext.slice(0, 500),
  });

  const code = status ? ` (Code ${status})` : "";
  return `Das hat gerade nicht geklappt${code}. Bitte versuche es in einer Minute noch einmal. Klappt es weiterhin nicht, melde dich kurz bei uns.`;
}
